import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Mail, Loader2, ArrowLeft, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESEND_COOLDOWN_SECONDS = 30;

/**
 * Email-OTP verification screen (Hybrid 2FA — user request #5).
 *
 * Props:
 *   challenge        — { temp_token, user_type, email_masked, expires_in_seconds } from /auth/login
 *   onBack()         — return to login form
 *   onComplete(data) — called with the final /verify-code response so the parent
 *                      can persist localStorage + route the user properly.
 */
const EmailOtpView = ({ challenge, onBack, onComplete }) => {
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState(challenge?.temp_token || '');
  const [rememberDevice, setRememberDevice] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Resend cooldown ticker
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.error('Enter a valid 6-digit code');
      return;
    }
    setVerifying(true);
    try {
      const res = await axios.post(`${API}/auth/login/verify-code`, {
        temp_token: tempToken,
        code,
        remember_device: rememberDevice,
      });
      if (res.data?.success) {
        // Persist device_token so future logins skip OTP.
        if (res.data.device_token) {
          localStorage.setItem('dfl_device_token', res.data.device_token);
        }
        onComplete(res.data);
      } else {
        toast.error('Verification failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Invalid code');
      setCode('');
      inputRef.current?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setResending(true);
    try {
      const res = await axios.post(`${API}/auth/login/resend-code`, {
        temp_token: tempToken,
      });
      if (res.data?.temp_token) {
        setTempToken(res.data.temp_token);
      }
      toast.success('New code sent. Check your inbox.');
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setCode('');
      inputRef.current?.focus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-20 px-4">
        <Card className="max-w-md mx-auto" data-testid="email-otp-view">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">Check your inbox</CardTitle>
            <p className="text-gray-500 text-sm mt-2">
              We sent a 6-digit code to{' '}
              <span className="font-medium text-gray-800" data-testid="otp-email-masked">
                {challenge?.email_masked}
              </span>
            </p>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Verification code
                </label>
                <Input
                  ref={inputRef}
                  type="text"
                  inputMode="numeric"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  maxLength={6}
                  autoComplete="one-time-code"
                  data-testid="otp-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Code expires in {Math.round((challenge?.expires_in_seconds || 600) / 60)} minutes.
                </p>
              </div>

              <Button
                type="submit"
                className="w-full bg-green-600 hover:bg-green-700"
                disabled={verifying || code.length !== 6}
                data-testid="otp-verify-btn"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Verify & sign in
              </Button>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="rounded border-gray-300"
                  data-testid="otp-remember-device"
                />
                <span className="text-sm text-gray-600">
                  Remember this device for 7 days
                </span>
              </label>

              <div className="flex flex-col items-center gap-3 pt-2 border-t">
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={cooldown > 0 || resending}
                  className="text-sm text-green-700 hover:text-green-800 disabled:text-gray-400 disabled:cursor-not-allowed flex items-center gap-1"
                  data-testid="otp-resend-btn"
                >
                  {resending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </button>
                <button
                  type="button"
                  onClick={onBack}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                  data-testid="otp-back-btn"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Use a different account
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EmailOtpView;
